import * as React from 'react';
import {Slot} from '@radix-ui/react-slot';
import {cva, type VariantProps} from 'class-variance-authority';

import {cn} from 'utils/helpers';

const defaultVariant = 'bg-primary text-primary-foreground hover:bg-primary/90!';
const destructive = 'bg-destructive text-destructive-foreground hover:bg-destructive/90!';

const buttonVariants = cva(
  'flex items-center justify-center gap-2 border border-transparent! !rounded-md group-[.pf-btn-group]:!rounded-none group-[.pf-btn-group]:!ml-[-1px] group-[.pf-btn-group]:first:!rounded-s-md group-[.pf-btn-group]:last:!rounded-e-md text-md font-medium px-[0.75rem] py-[0.375rem] ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: defaultVariant,
        primary: defaultVariant,
        'primary-public':
          'bg-pathformance-teal text-foreground hover:bg-pathformance-navy-dark! hover:text-primary-foreground',
        destructive,
        // TODO: temporary alias for destructive
        danger: destructive,
        outline: 'border-input bg-background text-foreground hover:bg-accent! hover:text-accent-foreground!',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80!',
        'outline-secondary': 'border-input bg-secondary/50 hover:bg-accent! hover:text-accent-foreground!',
        ghost: 'hover:bg-accent! hover:text-accent-foreground!',
        link: 'text-primary underline-offset-4 hover:underline!',
      },
      size: {
        default: 'max-h-20',
        sm: 'max-h-18 rounded-md text-sm',
        lg: 'max-h-22 rounded-md',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({className, variant, size, asChild = false, ...props}, ref) => {
    const Comp = asChild ? Slot : 'button';
    return <Comp className={cn(buttonVariants({variant, size, className}))} ref={ref} {...props} />;
  },
);
Button.displayName = 'Button';

export {Button, buttonVariants};
